import React, { useEffect, useState } from 'react'
import {
  Form,
  Button,
  Modal,
  Input,
  Icon,
  Tooltip,
  Radio,
  message,
  Select
} from 'antd'

import { formatLabel } from '../../../Courses/Create/nestedComponents/PageConstructor/internal/PageMaker/utils'
import { CreatableSelect } from '../../../../components'
import storyFormOptions from '../../../Courses/Create/nestedComponents/PageConstructor/internal/PageMaker/components/Story/storyFormOptions'
import { config } from '../../../../constants'
import { getToken } from '../../../../services/api/user'
import './StoryCreationForm.scss'
import { useTranslation } from 'react-i18next'
import { generateVideoStory } from '../../../../services/api/aiVideoGeneration'
import {
  LANGUAGE_OPTIONS,
  getLanguageLabelByCode
} from '../../constants/videoLanguages'
import TranslationWorkspace from './TranslationWorkspace'
import { getDirectionClassName, getLanguageDirection } from './storyDirection'

const { Option } = Select

const StoryCreationForm = Form.create()(
  ({
    form,
    inputMethod,
    initialText,
    onCancel,
    uploadedFile,
    onAddStory: addStory,
    videoId,
    isTranslation = false,
    existingStory = null,
    onBeforeAddStory = null,
    startInEditMode = false
  }) => {
    const { getFieldDecorator } = form

    const [isGeneratingStory, setIsGeneratingStory] = useState(false)
    const [story, setStory] = useState(null)
    const [storyLanguage, setStoryLanguage] = useState(null)
    const [isStoryVisible, setIsStoryVisible] = useState(false)
    const [isTranslationLoading, setIsTranslationLoading] = useState(false)
    const [isTranslationMode, setIsTranslationMode] = useState(isTranslation)
    const [translationSource, setTranslationSource] = useState(
      existingStory || ''
    )

    // Mode state: 'form' or 'chat'
    const [generationMode, setGenerationMode] = useState('form')
    // Chat mode state
    const [chatMessages, setChatMessages] = useState([])
    const [chatInput, setChatInput] = useState('')
    const [isChatLoading, setIsChatLoading] = useState(false)
    const [expandedStoryId, setExpandedStoryId] = useState(null)
    const chatContainerRef = React.useRef(null)

    const { t } = useTranslation()
    const getLanguageLabel = languageCode => {
      return getLanguageLabelByCode(languageCode)
    }

    const getLanguageName = languageCode => {
      return getLanguageLabel(languageCode || 'en')
    }

    const options = {
      storyObjective: {
        value: '',
        description: t('v5:storyObjectivesDescription')
      },
      ...storyFormOptions
    }

    const transformStoryElements = vals => {
      if (!vals) return null
      const hasValues = Object.values(vals).some(v => {
        if (v === undefined || v === null || v === '') return false
        if (Array.isArray(v) && v.length === 0) return false
        return true
      })
      if (!hasValues) return null

      const result = { ...vals }
      const names = Array.isArray(vals.characterNames)
        ? vals.characterNames
        : vals.characterNames
        ? [vals.characterNames]
        : []
      result.character_names = names
      delete result.characterNames
      return result
    }

    // Reset chat state when switching to chat mode
    useEffect(() => {
      if (
        !isTranslationMode &&
        generationMode === 'chat' &&
        chatMessages.length === 0
      ) {
        setChatMessages([
          {
            type: 'assistant',
            content:
              'Hello! I can help you generate a story for your video. You can describe what you need or give me some instructions based on your content.',
            timestamp: new Date(),
            id: Date.now(),
            isStreaming: false
          }
        ])
        setChatInput('')
        setExpandedStoryId(null)
      }
    }, [generationMode, isTranslationMode])

    useEffect(() => {
      if (
        startInEditMode &&
        !isTranslationMode &&
        typeof initialText === 'string' &&
        initialText.trim()
      ) {
        setStory(initialText)
        setStoryLanguage(form.getFieldValue('language') || 'en')
        setIsStoryVisible(true)
      }
    }, [startInEditMode, isTranslationMode, initialText, form])

    // Auto-scroll to bottom of chat
    useEffect(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight
      }
    }, [chatMessages])

    const handleSubmit = e => {
      e.preventDefault()
      form.validateFields((err, values) => {
        if (!err) {
          const storyObjective = values.storyObjective
          const languageCode = values.language || 'en'
          const languageName = getLanguageName(languageCode)
          const storyElements = {
            ...transformStoryElements(values),
            language: languageName
          }

          setIsGeneratingStory(true)

          generateVideoStory({
            params: {
              story_objective: storyObjective,
              story_elements: storyElements,
              ...(inputMethod === 'text' && { course_content: initialText }),
              ...(inputMethod === 'upload' && {
                path: uploadedFile && uploadedFile.path
              }),
              is_continuation: false,
              video_id: videoId
            }
          })
            .then(res => {
              if (!res.data.story) {
                const storyObj = res.data.body[0]
                const storyString =
                  storyObj.beginning + storyObj.middle + storyObj.end
                setStory(removeReferenceList(storyString))
              } else {
                const storyStr = res.data.story
                setStory(storyStr)
              }
              setStoryLanguage(languageCode)
              setIsStoryVisible(true)
            })
            .catch(err => {
              message.error(
                err?.response?.data?.message ||
                  'Something went wrong. Please try again.'
              )
              console.log(err)
            })
            .finally(() => setIsGeneratingStory(false))
        }
      })
    }

    const handleChatSubmit = async e => {
      e && e.preventDefault()
      if (!chatInput.trim() || isChatLoading) return
      const content = chatInput.trim()
      setChatInput('')
      await submitChatMessage(content)
    }

    const streamStoryGeneration = async ({ data, onProgress }) => {
      const token = await getToken()
      const baseURL =
        config.questionAnswerAiUrl + '?service=story-generation-chat'

      const response = await fetch(baseURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      })

      if (!response.ok || !response.body) {
        throw new Error('Failed to generate story')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let accumulatedText = ''
      let chatText = ''
      let storyText = ''
      let foundDelimiter = false

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        accumulatedText += chunk

        if (accumulatedText.includes('<<END>>')) {
          const parts = accumulatedText.split('<<END>>')
          chatText = parts[0]
          storyText = parts.slice(1).join('<<END>>') || ''
          foundDelimiter = true
        }

        if (onProgress) {
          onProgress({
            accumulatedText,
            chatText,
            storyText,
            foundDelimiter,
            isComplete: false
          })
        }
      }

      if (onProgress) {
        onProgress({
          accumulatedText,
          chatText,
          storyText,
          foundDelimiter,
          isComplete: true
        })
      }

      return {
        chatText: foundDelimiter ? chatText : accumulatedText,
        storyText: foundDelimiter ? storyText : '',
        accumulatedText,
        foundDelimiter
      }
    }

    const submitChatMessage = async content => {
      const messageId = `assistant-${Date.now()}-${Math.floor(
        Math.random() * 1000
      )}`
      setIsChatLoading(true)

      setChatMessages(prev => {
        const hasUserMsg = prev.some(
          m => m.content === content && m.type === 'user'
        )
        const newMsgs = hasUserMsg
          ? [...prev]
          : [
              ...prev,
              {
                type: 'user',
                content: content,
                timestamp: new Date(),
                id: `user-${Date.now()}-${Math.floor(Math.random() * 1000)}`
              }
            ]
        return [
          ...newMsgs,
          {
            type: 'assistant',
            content: '',
            timestamp: new Date(),
            id: messageId,
            isStreaming: true
          }
        ]
      })

      try {
        const storyObjective = form.getFieldValue('storyObjective') || ''
        const course_content =
          inputMethod === 'text'
            ? initialText
            : `Content from uploaded file: ${uploadedFile?.name || 'document'}`

        const previousStoryMessage = [...chatMessages]
          .reverse()
          .find(msg => msg.type === 'assistant' && msg.storyContent)

        const previous_story = previousStoryMessage?.storyContent || null
        const messageWithContext = storyObjective
          ? `Story Objective: ${storyObjective}\n\nInstruction: ${content}`
          : content

        const selectedLanguageCode = form.getFieldValue('language') || 'en'
        const languageName = getLanguageName(selectedLanguageCode)
        const data = {
          ...(inputMethod === 'text' &&
            !previous_story && { course_content: course_content }),
          ...(inputMethod === 'upload' &&
            !previous_story && {
              content_type: 'pdf_file',
              file_path:
                uploadedFile && uploadedFile.path ? [uploadedFile.path] : [],
              is_public: false
            }),
          message: messageWithContext,
          generated_story: previous_story,
          language: languageName
        }

        await streamStoryGeneration({
          data,
          onProgress: ({
            accumulatedText,
            chatText,
            storyText,
            foundDelimiter,
            isComplete
          }) => {
            setChatMessages(prev =>
              prev.map(msg =>
                msg.id === messageId
                  ? {
                      ...msg,
                      content: foundDelimiter ? chatText : accumulatedText,
                      storyContent: foundDelimiter ? storyText : null,
                      isStreaming: !foundDelimiter && !isComplete,
                      isStreamingStory: foundDelimiter && !isComplete
                    }
                  : msg
              )
            )
          }
        })
      } catch (error) {
        console.error('Chat error:', error)
        setChatMessages(prev =>
          prev.map(msg =>
            msg.id === messageId
              ? {
                  ...msg,
                  content: 'Sorry, I encountered an error. Please try again.',
                  isStreaming: false,
                  isStreamingStory: false
                }
              : msg
          )
        )
      } finally {
        setIsChatLoading(false)
      }
    }

    function removeReferenceList(text) {
      if (!text) return ''
      var pattern = /References:(.*?)(?=<\/p>)/gs
      var textWithoutReferences = text.replace(pattern, '').trim()
      return textWithoutReferences
    }

    const handleAddStory = (
      storyToUse = story,
      languageCode = null,
      options = {}
    ) => {
      const resolvedLanguageCode =
        languageCode || storyLanguage || form.getFieldValue('language') || 'en'

      const applyStory = () => {
        if (addStory) {
          addStory(storyToUse, resolvedLanguageCode, options)
        }
      }

      if (onBeforeAddStory) {
        onBeforeAddStory(applyStory, storyToUse, resolvedLanguageCode)
      } else {
        applyStory()
      }
    }

    const handleQuickTranslate = async () => {
      if (isTranslationLoading) return
      if (!String(translationSource || '').trim()) {
        message.info('Add a story before translating.')
        return
      }
      const langCode = form.getFieldValue('language') || 'en'
      const langName = getLanguageName(langCode)

      setStory(null)
      setIsStoryVisible(false)
      setIsTranslationLoading(true)

      try {
        const data = {
          message: `Translate this story to ${langName}.`,
          generated_story: translationSource,
          language: langName
        }

        const result = await streamStoryGeneration({ data })
        const translatedStory = (
          result.storyText ||
          result.accumulatedText ||
          ''
        ).trim()

        if (!translatedStory) {
          throw new Error('No translated story returned')
        }

        setStory(translatedStory)
        setStoryLanguage(langCode)
      } catch (error) {
        console.error('Translation error:', error)
        message.error('Failed to translate story. Please try again.')
      } finally {
        setIsTranslationLoading(false)
      }
    }

    const handleOpenStoryTranslation = () => {
      const sourceStory = String(story || '').trim()

      if (!sourceStory) {
        message.info('Add a story before translating.')
        return
      }

      setTranslationSource(sourceStory)
      setStory(null)
      setIsStoryVisible(false)
      setIsTranslationMode(true)
    }

    const toggleStoryExpansion = messageId => {
      setExpandedStoryId(prev => (prev === messageId ? null : messageId))
    }

    const renderChat = () => (
      <div className="chat-container">
        <div className="chat-messages" ref={chatContainerRef}>
          {chatMessages.map(msg => (
            <div key={msg.id} className={`chat-message-item ${msg.type}`}>
              <div className="message-content">
                {msg.type === 'assistant' && msg.isStreaming && !msg.content ? (
                  <div className="loading-dots">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </div>
                ) : (
                  <>
                    {msg.content}
                    {msg.isStreaming && msg.content && (
                      <span className="streaming-cursor" />
                    )}
                  </>
                )}
              </div>
              {msg.type === 'assistant' &&
                (msg.storyContent || msg.isStreamingStory) && (
                  <div className="generated-story-card">
                    <div
                      className="generated-story-card__header"
                      onClick={() => toggleStoryExpansion(msg.id)}
                    >
                      <span className="title">
                        <Icon type="book" /> Generated Story
                      </span>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        {msg.isStreamingStory && (
                          <span className="status">Generating...</span>
                        )}
                        <Icon
                          type={expandedStoryId === msg.id ? 'up' : 'down'}
                        />
                      </div>
                    </div>
                    {expandedStoryId === msg.id && (
                      <>
                        <div className="generated-story-card__content">
                          {msg.storyContent}
                        </div>
                        {msg.storyContent &&
                          !msg.isStreaming &&
                          !msg.isStreamingStory && (
                            <div className="generated-story-card__footer">
                              <Button
                                type="default"
                                className="review-edit-btn"
                                onClick={() => {
                                  setStory(msg.storyContent)
                                  setStoryLanguage(
                                    form.getFieldValue('language') || 'en'
                                  )
                                  setIsStoryVisible(true)
                                }}
                              >
                                <Icon type="edit" /> Review & Edit
                              </Button>
                            </div>
                          )}
                      </>
                    )}
                  </div>
                )}
            </div>
          ))}
        </div>

        <Form onSubmit={handleChatSubmit} className="chat-input-wrapper">
          <Input
            value={chatInput}
            onChange={e => setChatInput(e.target.value)}
            placeholder="Type your instructions here..."
            onPressEnter={handleChatSubmit}
            disabled={isChatLoading}
            className="chat-input"
          />
          <Button
            type="primary"
            htmlType="submit"
            loading={isChatLoading}
            disabled={!chatInput.trim()}
            className="send-btn"
          >
            <Icon type="caret-right" />
          </Button>
        </Form>
      </div>
    )

    return (
      <Modal
        maskClosable={false}
        width={850}
        title={
          isStoryVisible
            ? 'Edit Story'
            : isTranslationMode
            ? 'Story Translation'
            : 'Story Generation'
        }
        centered
        visible
        onCancel={() => onCancel()}
        footer={null}
        className="story-creation-modal"
      >
        {!isStoryVisible && !isTranslationMode && (
          <div className="story-generation-header">
            <div className="mode-selector">
              <Radio.Group
                value={generationMode}
                onChange={e => setGenerationMode(e.target.value)}
                buttonStyle="solid"
              >
                <Radio.Button value="form">
                  <Icon type="form" /> Form Mode
                </Radio.Button>
                <Radio.Button value="chat">
                  <Icon type="message" /> Chat Mode
                </Radio.Button>
              </Radio.Group>
            </div>

            <div className="language-selector">
              <Form.Item label="Language" labelAlign="left">
                {getFieldDecorator('language', {
                  initialValue: 'en'
                })(
                  <Select style={{ width: 150 }} placeholder="Select Language">
                    {LANGUAGE_OPTIONS.map(({ label, value }) => (
                      <Option key={value} value={value}>
                        {label}
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </div>
          </div>
        )}

        {!isTranslationMode && !isStoryVisible && generationMode === 'form' && (
          <Form className="story-course-form" onSubmit={handleSubmit}>
            <div className="form-grid-container">
              <div className="full-width-item">
                <Form.Item
                  labelAlign="top"
                  label={
                    <span className="story-course-form-label">
                      <span>{t('v5:storyObjectives')} </span>
                      <Tooltip title={options.storyObjective.description}>
                        <Icon type="info-circle" />
                      </Tooltip>
                    </span>
                  }
                >
                  {getFieldDecorator('storyObjective', {
                    rules: [
                      {
                        required: true,
                        message: `Please input your ${t('v5:storyObjectives')}!`
                      }
                    ]
                  })(
                    <Input.TextArea
                      placeholder={`Enter your ${t('v5:storyObjectives')}`}
                      rows={3}
                    />
                  )}
                </Form.Item>
              </div>
              {Object.entries(options)
                .filter(([key]) => key !== 'storyObjective')
                .map(([key, values]) => (
                  <div key={key}>
                    <Form.Item
                      labelAlign="top"
                      label={
                        <span className="story-course-form-label">
                          <span>{t(`v5:${key}`)} </span>
                          <Tooltip title={t(`${values.description}`)}>
                            <Icon type="info-circle" />
                          </Tooltip>
                        </span>
                      }
                    >
                      {getFieldDecorator(key, {
                        rules: [
                          {
                            required: values.value && values.value.length > 0,
                            message: `Please input your ${formatLabel(key)}!`
                          }
                        ]
                      })(
                        key === 'characterNames' ? (
                          <CreatableSelect
                            fieldKey={key}
                            updateOptionValue={(value, field) =>
                              form.setFieldsValue({ [field]: value })
                            }
                            options={[]}
                            isMulti
                            placeholder={t(
                              'v4:add_character_names_and_press_enter'
                            )}
                          />
                        ) : values.value && values.value.length > 0 ? (
                          <CreatableSelect
                            fieldKey={key}
                            updateOptionValue={(value, field) =>
                              form.setFieldsValue({ [field]: value })
                            }
                            options={values.value.map(optionKey =>
                              t(optionKey)
                            )}
                            placeholder={`${t('v4:select_or_type_your')} ${t(
                              `v5:${key}`
                            )}`}
                          />
                        ) : values.value && values.value.length > 0 ? (
                          <CreatableSelect
                            fieldKey={key}
                            updateOptionValue={(value, field) =>
                              form.setFieldsValue({ [field]: value })
                            }
                            options={values.value.map(optionKey =>
                              t(optionKey)
                            )}
                            placeholder={`${t('v4:select_or_type_your')} ${t(
                              `v5:${key}`
                            )}`}
                          />
                        ) : (
                          <Input.TextArea
                            placeholder={`${t('v4:enter_your')} ${t(
                              `v5:${key}`
                            )}`}
                          />
                        )
                      )}
                    </Form.Item>
                  </div>
                ))}
            </div>
            <div className="form-button-container">
              <Button
                loading={isGeneratingStory}
                className="generate-btn"
                htmlType="submit"
                shape="round"
                type="primary"
              >
                Generate Story
              </Button>
            </div>
          </Form>
        )}

        {!isTranslationMode &&
          !isStoryVisible &&
          generationMode === 'chat' &&
          renderChat()}

        {isTranslationMode && !isStoryVisible && (
          <TranslationWorkspace
            existingStory={translationSource}
            translatedStory={story}
            translatedStoryLanguage={storyLanguage}
            translatedStoryLanguageLabel={getLanguageLabel(storyLanguage)}
            languageControl={getFieldDecorator('language', {
              initialValue: 'en'
            })(
              <Select style={{ width: '100%' }} placeholder="Select Language">
                {LANGUAGE_OPTIONS.map(({ label, value }) => (
                  <Option key={value} value={value}>
                    {label}
                  </Option>
                ))}
              </Select>
            )}
            isLoading={isTranslationLoading}
            onSourceChange={setTranslationSource}
            onTranslate={handleQuickTranslate}
            onReview={() => setIsStoryVisible(true)}
            onAddStory={() => handleAddStory(story, storyLanguage)}
          />
        )}

        {isStoryVisible && (
          <StoryEditor
            data={story}
            onChange={setStory}
            onAddStory={() =>
              handleAddStory(
                story,
                storyLanguage || form.getFieldValue('language') || 'en',
                { proceedToNextStep: true }
              )
            }
            onBack={() => setIsStoryVisible(false)}
            onTranslate={handleOpenStoryTranslation}
            direction={getLanguageDirection(storyLanguage)}
            showBackButton={!startInEditMode}
          />
        )}
      </Modal>
    )
  }
)

const StoryEditor = ({
  data,
  onChange,
  onAddStory,
  onBack,
  onTranslate,
  direction = 'auto',
  showBackButton = true
}) => {
  const [story, setStory] = useState(null)

  useEffect(() => {
    if (data) {
      setStory(data)
    }
  }, [data])

  const handleChange = value => {
    setStory(value)
    onChange(value)
  }

  return (
    <div
      className={`story-editor-container ${getDirectionClassName(direction)}`}
      dir={direction}
    >
      <div className="editor-card">
        <Input.TextArea
          value={story}
          onChange={e => handleChange(e.target.value)}
          rows={12}
        ></Input.TextArea>
      </div>

      <div className="editor-actions">
        {showBackButton && (
          <Button
            className="back-to-generator-btn"
            shape="round"
            onClick={onBack}
          >
            <Icon type="arrow-left" /> Back to Generator
          </Button>
        )}
        <Button
          className="translate-story-btn"
          shape="round"
          onClick={onTranslate}
        >
          <Icon type="translation" /> Translate
        </Button>
        <Button
          type="primary"
          className="add-to-video-btn"
          shape="round"
          onClick={() => onAddStory()}
        >
          Add to Video
        </Button>
      </div>
    </div>
  )
}

export default StoryCreationForm
